import React, {ReactElement} from 'react';
import {ReactNode} from 'react';
import {debounce} from 'lodash-es';
import {jssPreset, Theme, createGenerateClassName} from "@material-ui/core";
import {create} from "jss"; // "jss" is a dependency of material ui, so no need to include it in package.json (doing so may actually lead to a version mismatch)
import {StylesProvider, ThemeProvider} from "@material-ui/core/styles";
import {IntlProvider} from 'react-intl/dist';
import fromEntries from 'object.fromentries';
import {uniqueCssClassPrefix} from "app/diary.module.helpers";
import AppThemeService from 'app/services/theme/appTheme.service';
import {LocaleService} from 'app/services/locale/locale.service';

export default class ReactUtilsService {
  /**
   * Runs through each of `propNames` on object and assigns an empty object if `undefined`.
   * @param obj - any object
   * @param propNames - array of property names in order
   */
  static fillWithEmpty(obj: any, propNames: string[]): void {
    const prop = propNames[0];
    if (prop && obj[prop] === undefined) {
      obj[prop] = {};
    }
    propNames.shift();
    if (propNames.length) {
      this.fillWithEmpty(obj[prop], propNames);
    }
  }

  /**
   * Helper to avoid synthetic event null issue on react handlers when using debounce.
   * Warning: `currentTarget` may be null and target's value (if input) may not update correctly.
   * REF: https://stackoverflow.com/questions/35435074/using-debouncer-with-react-event
   * @param fn
   * @param time
   */
  static debounceEventHandler(fn: (e: any) => void, time: number) {
    const debounced = debounce(fn, time);
    return (evt: any) => {
      evt.persist();
      return debounced(evt);
    }
  }

  /**
   * Renders a ReactNode based on a truthy condition. Else can be optionally provided either as a ReactNode or a function returning a ReactNode.
   * If you are using `elseContent` it is recommended that you wrap you're ReactNodes in functions to avoid them getting executed even when conditions are not met.
   * For example, if `elseContent` is provided, and you have functions within it that will error without some specific data, you should wrap the ReactNode in a function,
   * so it only gets called when the condition becomes falsy. Otherwise you'll get errors even though the condition is still true, as both ReactNodes will get executed,
   * even though they are not both rendered.
   * @param condition
   * @param content
   * @param elseContent
   */
  static renderIf(condition: any, content: ReactNode | (() => ReactNode), elseContent: ReactNode | (() => ReactNode) = null): ReactNode {
    return condition ? (
      typeof content === 'function' ? content() : content
    ) : (
      typeof elseContent === 'function' ? elseContent() : elseContent
    );
  }

  /**
   * Make links open in a new window when using ReactMarkdown
   * Use like this:
   * <ReactMarkdown source={text}
   renderers={{
        paragraph: 'span',
        link: UtilsService.reactMarkDownBlankTargets
      }}
   escapeHtml={false} />
   */
  static reactMarkDownBlankTargets(props: { href: string, children: string }): ReactElement {
    return (<a href={props.href} target="_blank" rel="noopener noreferrer">{props.children}</a>);
  }


  /**
   * Returns a new object only with property names that exist in the provided `keys` property
   * @param obj
   * @param keys
   */
  static getObjectByKeys(obj: Object, keys: string[]): Object {
    return fromEntries(
      Object.entries(obj)
        .filter(([name, val]) => keys.includes(name))
    );
  }

  static getAngularService = (svcName: any) => {
    const svc = window.angular.element(document.body).injector().get(svcName);
    return window.angular ? svc : null;
  }

  static getNewIsolateScope = () => {
    const $rootScope = ReactUtilsService.getAngularService('$rootScope');
    return $rootScope.$new(true);
  }


  static getNumericalName(num: number) {
    if (num === null) {
      return '';
    }
    const special = ['zeroth','first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth'];
    const deca = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];

    const stringifyNumber = (n: number) => {
      if (n < 20) return special[n];
      if (n%10 === 0) return deca[Math.floor(n/10)-2] + 'ieth';
      return deca[Math.floor(n/10)-2] + 'y-' + special[n%10];
    }
    return stringifyNumber(num + 1);
  }

  static rangeCheck(val: number, max: number, min: number): number {
    if (val < min) {
      val = min;
    } else if(val > max) {
      val = max;
    }
    return val;
  }


  /**
   * Common container for most react components, in one place for convenient updating.
   * @param child - your component
   * @param theme - optionally override the default theme
   */
  static containerWrap(child: ReactNode, classPrefix = '', theme = AppThemeService.getAppTheme()): ReactElement {

    const generateClassName = createGenerateClassName({
      seed: classPrefix || uniqueCssClassPrefix()
    });

    const jss = create({
      ...jssPreset(),
      // Define a custom insertion point that JSS will look for when injecting the styles into the DOM.
      insertionPoint: document.getElementById("custom-jss-insertion-point")
    });

    return (
      <IntlProvider locale={LocaleService.getLocale()}>
        <StylesProvider jss={jss} generateClassName={generateClassName} >
          <ThemeProvider theme={theme}>
            {child}
          </ThemeProvider>
        </StylesProvider>
      </IntlProvider>
    );
  }

}

// so you can import directly
export const renderIf = ReactUtilsService.renderIf;
export const getNewIsolateScope = ReactUtilsService.getNewIsolateScope;
